import { useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as THREE from 'three';
import { frameMesh } from './cameraFrame';
import { getEntry } from './anatomyRegistry';

const FILTER_KEYWORDS = ['shader', 'shadow', 'ground', 'helper'];

const _v = new THREE.Vector3();

function extractScene(mv) {
  if (!mv) return null;
  const symbols = Object.getOwnPropertySymbols(mv);
  for (const sym of symbols) {
    const val = mv[sym];
    if (val && val.isScene) return val;
    if (val && val.scene && val.scene.isScene) return val.scene;
  }
  const keys = Object.getOwnPropertyNames(mv);
  for (const key of keys) {
    try {
      const val = mv[key];
      if (val && val.isScene) return val;
      if (val && val.scene && val.scene.isScene) return val.scene;
    } catch (e) {}
  }
  return null;
}

function normalizeName(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\.[0-9]+$|\b[0-9]{3}$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isFiltered(name, matName) {
  const low = (name || '').toLowerCase();
  const lowMat = (matName || '').toLowerCase();
  return FILTER_KEYWORDS.some((f) => low.includes(f) || lowMat.includes(f));
}

function findMeshByName(scene, targetRaw, expectedPos) {
  const target = normalizeName(targetRaw);
  if (!target) return null;

  let best = null;
  scene.traverse((obj) => {
    if (!obj.isMesh || best) return;
    if (isFiltered(obj.name, obj.material?.name)) return;
    if (normalizeName(obj.name) === target || normalizeName(obj.material?.name) === target) {
      best = obj;
    }
  });
  if (best) {
    // Disambiguation: when hand/foot meshes normalize to the same name (e.g.
    // "obj_4th proximal phalanx_L" vs foot "obj_4th Proximal  phalanx_L"),
    // the first-traversed mesh wins regardless of which the user meant. If we
    // know the expected position from the registry, prefer the exact-name match
    // whose world position is closest to it.
    if (expectedPos) return disambiguateByPosition(scene, target, best, expectedPos);
    return best;
  }

  const candidates = [];
  scene.traverse((obj) => {
    if (!obj.isMesh) return;
    if (isFiltered(obj.name, obj.material?.name)) return;
    const nn = normalizeName(obj.name);
    if (nn && (nn.includes(target) || target.includes(nn))) {
      candidates.push({ obj, diff: Math.abs(nn.length - target.length) });
    }
  });
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.diff - b.diff);
    best = candidates[0].obj;
    if (expectedPos) {
      const asObjs = candidates.map((c) => c.obj);
      best = chooseClosestTo(asObjs, best, expectedPos);
    }
  }
  return best;
}

function chooseClosestTo(meshes, fallback, expectedPos) {
  if (!expectedPos || meshes.length <= 1) return fallback;
  let best = fallback;
  let bestDist = Infinity;
  for (const obj of meshes) {
    obj.updateWorldMatrix(true, false);
    const p = obj.getWorldPosition(_v);
    const d = (p.x - expectedPos[0]) ** 2 + (p.y - expectedPos[1]) ** 2 + (p.z - expectedPos[2]) ** 2;
    if (d < bestDist) { bestDist = d; best = obj; }
  }
  return best;
}

function disambiguateByPosition(scene, normalizedTarget, fallback, expectedPos) {
  const matches = [];
  scene.traverse((obj) => {
    if (!obj.isMesh) return;
    if (isFiltered(obj.name, obj.material?.name)) return;
    if (normalizeName(obj.name) === normalizedTarget || normalizeName(obj.material?.name) === normalizedTarget) {
      matches.push(obj);
    }
  });
  return chooseClosestTo(matches, fallback, expectedPos);
}

// ============================================================================
// Orchestration hook — the search-result focus pipeline.
//
// Ordered sequence (see ANATOMY_SEARCH.md):
//   1. getEntry(meshKey) -> { camera?, reveal?, side, ... }
//   2. findMeshByName(scene, focusMesh, camera.target) -> mesh
//      (target disambiguates hand/foot meshes w/ identical normalized names)
//   3. onReveal(mesh, entry) -> brain split / muscle peel (await animation)
//   4. mesh.updateWorldMatrix(true, false)  // post-reveal matrices
//   5. frameMesh({ mv, mesh, entry, side, onSettled })
//   6. onSettled({ anchor, normal }) -> onFound(mesh, anchor, normal)
//        -> applySelection -> highlightMesh + hotspot label
//
// The manual click path (handleModelClick -> applySelection) is entirely
// separate and is NOT affected by anything in this file.
//
// Returns { focusMeshIfPending, focusMesh }:
//   - focusMeshIfPending: fires ONCE on load from router state (focusMesh).
//   - focusMesh(meshKey): reusable, fires any time — used by the in-page
//     search bar to refocus within the same model with smooth camera rotation.
// ============================================================================
export function useFocusMeshOnLoad({ modelRef, onFound, onReveal, enabled = true }) {
  const location = useLocation();
  const pendingRef = useRef(enabled ? location.state?.focusMesh || null : null);
  const firedRef = useRef(false);
  const onFoundRef = useRef(onFound);
  const onRevealRef = useRef(onReveal);

  useEffect(() => { onFoundRef.current = onFound; }, [onFound]);
  useEffect(() => { onRevealRef.current = onReveal; }, [onReveal]);

  // Reusable core: locate, reveal, frame, label. No one-shot guard so it can be
  // invoked repeatedly (e.g. consecutive same-page searches).
  const focusMesh = useCallback(async (target) => {
    const mv = modelRef.current;
    if (!target || !mv) return;

    const scene = extractScene(mv);
    if (!scene) return;

    // 1. Look up the registry entry first so we can use its camera target to
    //    disambiguate hand/foot meshes whose names normalize identically.
    const entry = getEntry(target);

    // 2. Locate the target mesh in the loaded scene.
    const expectedPos = entry && entry.camera && Array.isArray(entry.camera.target)
      ? entry.camera.target : null;
    const mesh = findMeshByName(scene, target, expectedPos);
    if (!mesh) return;

    // 3. Reveal hidden geometry (brain split, muscle peel) and wait for the
    //    animation to settle before measuring the bounding box.
    if (onRevealRef.current) {
      await onRevealRef.current(mesh, entry);
      // Extra buffer so any trailing animation frame has flushed and world
      // matrices reflect the final post-reveal positions.
      await new Promise((res) => setTimeout(res, 60));
    }

    // 4. Frame the camera (smooth eased tween), then highlight + label on
    //    settle via onFound -> applySelection.
    frameMesh({
      mv,
      mesh,
      entry,
      side: entry && entry.side,
      onSettled: ({ anchor, normal }) => {
        if (onFoundRef.current && anchor) {
          onFoundRef.current(mesh, anchor, normal);
        }
      },
    });
  }, [modelRef]);

  // One-shot on-load trigger driven by router navigation state.
  const focusMeshIfPending = useCallback(async () => {
    if (firedRef.current) return;
    const target = pendingRef.current;
    if (!target) return;
    firedRef.current = true;
    await focusMesh(target);
  }, [focusMesh]);

  return { focusMeshIfPending, focusMesh };
}
